import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Users, Search, Check, Loader2, Info } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const CreateGroupModal = ({ open, onClose, onSave }) => {
  const [title, setTitle] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [inviteOnly, setInviteOnly] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchWhatsapps = async () => {
      setLoadingWhatsapps(true);
      try {
        const { data } = await api.get("/whatsapp/");
        const connected = data.filter(w => w.status === "CONNECTED");
        setWhatsapps(connected);
        if (connected.length > 0) {
          setSelectedWhatsappId(connected[0].id);
        }
      } catch (err) {
        toastError(err);
      }
      setLoadingWhatsapps(false);
    };
    fetchWhatsapps();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const fetchContacts = async () => {
      setLoadingContacts(true);
      try {
        const { data } = await api.get("/contacts/", {
          params: { searchParam, pageNumber: 1 }
        });
        const filtered = (data.contacts || []).filter(c => !c.isGroup);
        setContacts(filtered);
      } catch (err) {
        toastError(err);
      }
      setLoadingContacts(false);
    };

    const delayDebounceFn = setTimeout(() => {
      fetchContacts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, open]);

  const handleClose = () => {
    setTitle("");
    setSelectedWhatsappId("");
    setSelectedContacts([]);
    setSearchParam("");
    setInviteOnly(false);
    setInviteLink(null);
    setShowSuccessView(false);
    setCopied(false);
    onClose();
  };

  const handleSelectContact = (contact) => {
    const isSelected = selectedContacts.some(c => c.id === contact.id);
    if (isSelected) {
      setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const isContactSelected = (contactId) => {
    return selectedContacts.some(c => c.id === contactId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedWhatsappId) {
      toast.warning("Debe seleccionar una conexión de WhatsApp conectada.");
      return;
    }

    if (!title.trim()) {
      toast.warning("Debe ingresar un nombre para el grupo.");
      return;
    }

    if (selectedContacts.length === 0) {
      toast.warning("Debe seleccionar al menos un participante.");
      return;
    }

    setIsSubmitting(true);
    try {
      const participants = selectedContacts.map(c => c.number);
      const { data } = await api.post(`/whatsapp/${selectedWhatsappId}/groups`, {
        title: title.trim(),
        participants,
        inviteOnly
      });

      if (inviteOnly && data.inviteLink) {
        setInviteLink(data.inviteLink);
        setShowSuccessView(true);
        toast.success("Grupo creado y enlaces de invitación enviados.");
        if (onSave) {
          onSave(data.contact);
        }
      } else {
        toast.success("Grupo creado exitosamente.");
        if (onSave) {
          onSave(data.contact || data);
        }
        handleClose();
      }
    } catch (err) {
      toastError(err);
    }
    setIsSubmitting(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        {showSuccessView ? (
          <div className="flex flex-col flex-1 overflow-hidden animate-fadeIn">
            {/* Success Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-none">
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                ¡Grupo Creado!
              </h2>
              <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Success Body */}
            <div className="flex-1 overflow-y-auto px-6 py-8 space-y-6 flex flex-col items-center text-center bg-slate-50 dark:bg-slate-800/30">
              <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center border border-emerald-100 dark:border-emerald-900/50 animate-bounce">
                <Check className="w-8 h-8 stroke-[3]" />
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-bold text-slate-950 dark:text-slate-50">
                  Grupo "{title}" creado con éxito
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
                  El grupo se ha creado de manera segura. Las invitaciones individuales se están enviando a los contactos seleccionados en segundo plano.
                </p>
              </div>

              {/* Link Box */}
              <div className="w-full space-y-2 text-left">
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Enlace de Invitación del Grupo
                </label>
                <div className="flex gap-2 bg-white dark:bg-slate-800 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 shadow-inner">
                  <input
                    type="text"
                    readOnly
                    value={inviteLink || ""}
                    className="flex-1 bg-transparent border-none text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-0 overflow-ellipsis"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (inviteLink) {
                        navigator.clipboard.writeText(inviteLink);
                        setCopied(true);
                        toast.success("Enlace copiado al portapapeles.");
                        setTimeout(() => setCopied(false), 2000);
                      }
                    }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                      copied
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                    }`}
                  >
                    {copied ? "¡Copiado!" : "Copiar"}
                  </button>
                </div>
              </div>
            </div>

            {/* Success Footer */}
            <div className="flex items-center justify-end px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex-none">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-none">
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-600" />
                Crear Grupo de WhatsApp
              </h2>
              <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
              {/* WhatsApp Connection Select */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Conexión de WhatsApp
                </label>
                {loadingWhatsapps ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando conexiones...
                  </div>
                ) : whatsapps.length === 0 ? (
                  <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950/20 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
                    No hay conexiones de WhatsApp activas (conectadas) en este momento. Por favor conecta una cuenta en la sección Conexiones.
                  </div>
                ) : (
                  <select
                    value={selectedWhatsappId}
                    onChange={(e) => setSelectedWhatsappId(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  >
                    {whatsapps.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} ({w.channel || "WhatsApp"})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Group Name input */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Nombre del Grupo
                </label>
                <input
                  type="text"
                  placeholder="Ej. Soporte al Cliente, Ventas..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={25}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  required
                />
              </div>

              {/* Invite Link Toggle */}
              <div className="flex items-center justify-between p-3.5 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-xl border border-indigo-100/50 dark:border-indigo-900/30 gap-4">
                <div className="flex flex-col gap-0.5 flex-1">
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                    <Info className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    Generar enlace de invitación
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 leading-normal">
                    Crea el grupo de forma segura y envía el enlace individualmente a cada contacto. Evita suspensiones del número por spam.
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={inviteOnly}
                    onChange={(e) => setInviteOnly(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-6 bg-slate-200 peer-focus:outline-none dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-indigo-600 rounded-full transition-colors animate-all"></div>
                </label>
              </div>

              {/* Selected Contacts Badges */}
              {selectedContacts.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                    Participantes Seleccionados ({selectedContacts.length})
                  </label>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    {selectedContacts.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-2.5 py-1 rounded-full border border-indigo-100/50 dark:border-indigo-900/50 animate-fadeIn"
                      >
                        {c.name}
                        <button
                          type="button"
                          onClick={() => handleSelectContact(c)}
                          className="text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-200 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contacts list & search */}
              <div className="flex flex-col">
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                  Seleccionar Contactos
                </label>
                
                <div className="relative mb-2 flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar contactos..."
                    value={searchParam}
                    onChange={(e) => setSearchParam(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                {/* Contacts Selection Panel */}
                <div className="h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700/50 flex-1">
                  {loadingContacts ? (
                    <div className="flex items-center justify-center h-full gap-2 text-sm text-slate-400">
                      <Loader2 className="w-4 h-4 animate-spin" /> Buscando contactos...
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 p-4 text-center">
                      <Info className="w-6 h-6 mb-1 text-slate-300" />
                      <span className="text-sm">No se encontraron contactos</span>
                    </div>
                  ) : (
                    contacts.map((contact) => {
                      const selected = isContactSelected(contact.id);
                      return (
                        <div
                          key={contact.id}
                          onClick={() => handleSelectContact(contact)}
                          className={`flex items-center justify-between px-3 py-2.5 text-sm cursor-pointer select-none transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/30 ${
                            selected ? "bg-indigo-50/30 dark:bg-indigo-950/10" : ""
                          }`}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900 dark:text-slate-100">
                              {contact.name}
                            </span>
                            <span className="text-xs text-slate-400">
                              {contact.number}
                            </span>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                              selected
                                ? "bg-indigo-600 border-indigo-600 text-white"
                                : "border-slate-300 dark:border-slate-600"
                            }`}
                          >
                            {selected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex-none">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || whatsapps.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Crear Grupo
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateGroupModal;

